import { useState, useMemo, useEffect, ReactElement } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import StoreSelector from './StoreSelector';
import WelcomeScreen from './WelcomeScreen';
import { ThemeToggle, CommandPalette, BottomNav, FloatingActionButton } from './common';
import { LanguageToggle } from './ui';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  DollarSign,
  Users,
  LogOut,
  Menu,
  X,
  Wrench,
  FileText,
  RotateCcw,
  Box,
  Shield,
  Settings as SettingsIcon,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';

interface TechPulseLogoProps {
  className?: string;
}

// TechPulse Logo Component
const TechPulseLogo = ({ className = '' }: TechPulseLogoProps): ReactElement => (
  <div className={`flex items-center gap-2 ${className}`}>
    <svg
      viewBox="0 0 100 100"
      className="w-9 h-9"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="tpGrad" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#1B6FF2" />
          <stop offset="50%" stopColor="#00C2CB" />
          <stop offset="100%" stopColor="#28C76F" />
        </linearGradient>
      </defs>
      <rect width="100" height="100" rx="22" fill="url(#tpGrad)" />
      <g fill="#FFFFFF">
        <rect x="12" y="16" width="34" height="10" rx="3" />
        <rect x="12" y="26" width="10" height="36" rx="3" />
        <rect x="54" y="16" width="10" height="52" rx="3" />
        <path d="M64 16 C86 16, 86 46, 64 46 L64 36 C74 36, 74 26, 64 26 Z" fillRule="evenodd" />
        <path d="M36 68 L36 52 L28 52 L40 36 L52 52 L44 52 L44 68 Z" />
      </g>
    </svg>
    <span className="font-heading text-xl font-semibold">
      <span className="text-brand-navy dark:text-white">Tech</span>
      <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-blue via-brand-cyan to-brand-green">
        Pulse
      </span>
    </span>
  </div>
);

interface NavItemConfig {
  path?: string;
  labelKey: string;
  icon: LucideIcon;
  permission: string;
  isGroup?: boolean;
  children?: NavItemConfig[];
}

interface NavItem extends Omit<NavItemConfig, 'children'> {
  label: string;
  children?: NavItem[];
}

interface Command {
  id: string;
  label: string;
  icon: ReactElement;
  keywords: string[];
  action: () => void;
}

// Navigation item definitions with translation keys
const navItemsConfig: NavItemConfig[] = [
  // Operational items
  { path: '/', labelKey: 'navigation:menu.dashboard', icon: LayoutDashboard, permission: 'dashboard_view' },
  { path: '/inventory', labelKey: 'navigation:menu.inventory', icon: Package, permission: 'inventory_view' },
  { path: '/bundles', labelKey: 'navigation:menu.bundles', icon: Box, permission: 'inventory_view' },
  { path: '/sales', labelKey: 'navigation:menu.sales', icon: ShoppingCart, permission: 'sales_view' },
  { path: '/repairs', labelKey: 'navigation:menu.repairs', icon: Wrench, permission: 'repairs_view' },
  { path: '/quotes', labelKey: 'navigation:menu.quotes', icon: FileText, permission: 'sales_view' },
  { path: '/invoices', labelKey: 'navigation:menu.invoices', icon: FileText, permission: 'invoices_view' },
  { path: '/returns', labelKey: 'navigation:menu.returns', icon: RotateCcw, permission: 'sales_manage' },
  { path: '/customers', labelKey: 'navigation:menu.customers', icon: Users, permission: 'customers_view' },
  { path: '/expenses', labelKey: 'navigation:menu.expenses', icon: DollarSign, permission: 'expenses_view' },

  // Admin section (collapsible group)
  {
    labelKey: 'navigation:menu.administration',
    icon: ShieldCheck,
    isGroup: true,
    permission: 'admin_section', // Special permission check
    children: [
      { path: '/users', labelKey: 'navigation:menu.users', icon: Users, permission: 'users_manage' },
      { path: '/roles', labelKey: 'navigation:menu.roles', icon: Users, permission: 'roles_manage' },
      { path: '/commissions', labelKey: 'navigation:menu.commissions', icon: DollarSign, permission: 'users_manage' },
      { path: '/logs', labelKey: 'navigation:menu.logs', icon: Shield, permission: 'users_manage' },
      { path: '/settings', labelKey: 'navigation:menu.settings', icon: SettingsIcon, permission: 'roles_manage' },
    ],
  },
];

export default function Layout(): ReactElement {
  const { t } = useTranslation(['navigation', 'common']);
  const { logout, userRole, userName, hasPermission, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  // Admin section collapse/expand state with localStorage persistence
  const [adminSectionExpanded, setAdminSectionExpanded] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('adminSectionExpanded');
      return saved !== null ? JSON.parse(saved) : true; // Default expanded
    } catch (error) {
      console.error('Failed to read from localStorage:', error);
      return true; // Default expanded
    }
  });

  // Check if user has any admin permissions
  const canAccessAdminSection = hasPermission('users_manage') || hasPermission('roles_manage');

  // Persist admin section expanded state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('adminSectionExpanded', JSON.stringify(adminSectionExpanded));
    } catch (error) {
      console.error('Failed to write to localStorage:', error);
    }
  }, [adminSectionExpanded]);

  // Create nav items with translated labels
  const navItems: NavItem[] = useMemo(() => {
    const translateItem = (item: NavItemConfig): NavItem => ({
      ...item,
      label: t(item.labelKey),
      children: item.children?.map(translateItem),
    });
    return navItemsConfig.map(translateItem);
  }, [t]);

  // Filter nav items based on permissions
  const filteredNavItems: NavItem[] = useMemo(() => {
    return navItems.filter(item => {
      if (item.isGroup) {
        // For admin group, check if user has any admin permissions
        return canAccessAdminSection;
      }
      if (!item.permission) return true;
      return hasPermission(item.permission);
    }).map(item => {
      // Filter children based on permissions
      if (item.isGroup && item.children) {
        return {
          ...item,
          children: item.children.filter(child => {
            if (!child.permission) return true;
            return hasPermission(child.permission);
          }),
        };
      }
      return item;
    });
  }, [navItems, hasPermission, canAccessAdminSection]);

  // Command palette commands (flatten nested items)
  const commands: Command[] = useMemo(() => {
    const flatItems = filteredNavItems.flatMap(item => {
      if (item.isGroup && item.children) {
        // Return children for admin group
        return item.children.filter(child => child.path);
      }
      return item.path ? [item] : [];
    });

    return flatItems.map(item => ({
      id: item.path!,
      label: t('navigation:header.goTo', { page: item.label }),
      icon: <item.icon size={18} />,
      keywords: [item.label.toLowerCase()],
      action: () => navigate(item.path!),
    }));
  }, [filteredNavItems, navigate, t]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-light-bg dark:bg-dark-bg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600 mx-auto mb-4"></div>
          <p className="text-light-muted dark:text-dark-muted">{t('common:status.loading')}</p>
        </div>
      </div>
    );
  }

  // Show welcome screen ONLY if user has no role or role is 'invite' AFTER loading
  if (!userRole || userRole === 'invite') {
    return <WelcomeScreen />;
  }

  return (
    <div className="flex h-screen bg-light-card dark:bg-dark-bg">
      {/* Skip to main content link for keyboard navigation */}
      <a
        href="#main-content"
        className="skip-link"
      >
        {t('navigation:accessibility.skipToContent', 'Skip to main content')}
      </a>

      {/* Command Palette */}
      <CommandPalette commands={commands} />

      {/* Desktop Sidebar */}
      <aside
        className="hidden md:flex w-64 bg-white dark:bg-dark-card shadow-card dark:shadow-dark-card flex-col"
        role="complementary"
        aria-label={t('navigation:accessibility.sidebar', 'Sidebar navigation')}
      >
        <div className="p-5 border-b border-light-border dark:border-dark-border">
          <TechPulseLogo />
        </div>
        <nav
          className="flex-1 p-4 space-y-1 overflow-y-auto"
          aria-label={t('navigation:accessibility.mainNavigation', 'Main navigation')}
        >
          {filteredNavItems.map(item => {
            if (item.isGroup && item.children) {
              // Render collapsible admin section
              const Icon = item.icon;
              const isAnyAdminPageActive = item.children.some(
                child => child.path && location.pathname === child.path
              );

              return (
                <div key={item.label}>
                  <button
                    onClick={() => setAdminSectionExpanded(!adminSectionExpanded)}
                    className={clsx(
                      'flex items-center space-x-3 p-3 rounded-lg transition-colors w-full',
                      isAnyAdminPageActive
                        ? 'bg-accent-50 text-accent-600 dark:bg-accent-900/20 dark:text-accent-400'
                        : 'text-light-muted hover:bg-light-hover dark:text-dark-muted dark:hover:bg-dark-hover'
                    )}
                    aria-expanded={adminSectionExpanded}
                  >
                    <Icon size={20} aria-hidden="true" />
                    <span className="font-medium flex-1 text-left">{item.label}</span>
                    {adminSectionExpanded ? (
                      <ChevronDown size={16} aria-hidden="true" />
                    ) : (
                      <ChevronRight size={16} aria-hidden="true" />
                    )}
                  </button>

                  {/* Collapsible children */}
                  {adminSectionExpanded && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.children.map(child => {
                        const ChildIcon = child.icon;
                        const isActive = location.pathname === child.path;
                        const navId = child.path === '/' ? 'dashboard' : child.path?.slice(1);
                        return (
                          <Link
                            key={child.path}
                            to={child.path!}
                            data-testid={`nav-${navId}`}
                            aria-current={isActive ? 'page' : undefined}
                            className={clsx(
                              'flex items-center space-x-3 p-3 rounded-lg transition-colors',
                              isActive
                                ? 'bg-accent-50 text-accent-600 dark:bg-accent-900/20 dark:text-accent-400'
                                : 'text-light-muted hover:bg-light-hover dark:text-dark-muted dark:hover:bg-dark-hover'
                            )}
                          >
                            <ChildIcon size={20} aria-hidden="true" />
                            <span className="font-medium">{child.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            } else {
              // Render regular menu item
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const navId = item.path === '/' ? 'dashboard' : item.path?.slice(1);
              return (
                <Link
                  key={item.path}
                  to={item.path!}
                  data-testid={`nav-${navId}`}
                  aria-current={isActive ? 'page' : undefined}
                  className={clsx(
                    'flex items-center space-x-3 p-3 rounded-lg transition-colors',
                    isActive
                      ? 'bg-accent-50 text-accent-600 dark:bg-accent-900/20 dark:text-accent-400'
                      : 'text-light-muted hover:bg-light-hover dark:text-dark-muted dark:hover:bg-dark-hover'
                  )}
                >
                  <Icon size={20} aria-hidden="true" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            }
          })}
        </nav>
        <div className="p-4 border-t border-light-border dark:border-dark-border">
          <button
            onClick={logout}
            className="flex items-center space-x-3 p-3 w-full text-error hover:bg-error-light dark:hover:bg-error/20 rounded-lg transition-colors"
          >
            <LogOut size={20} aria-hidden="true" />
            <span className="font-medium">{t('navigation:header.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setIsMobileMenuOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={t('navigation:accessibility.mobileMenu', 'Mobile navigation menu')}
        >
          <aside
            className="w-64 bg-white dark:bg-dark-card shadow-elevated dark:shadow-dark-elevated flex flex-col h-full animate-slide-up"
            onClick={e => e.stopPropagation()}
            role="complementary"
            aria-label={t('navigation:accessibility.sidebar', 'Sidebar navigation')}
          >
            <div className="p-5 border-b border-light-border dark:border-dark-border flex justify-between items-center">
              <TechPulseLogo />
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-light-muted dark:text-dark-muted hover:text-light-text dark:hover:text-dark-text"
                aria-label={t('navigation:accessibility.closeMenu', 'Close navigation menu')}
              >
                <X size={24} aria-hidden="true" />
              </button>
            </div>
            <nav
              className="flex-1 p-4 space-y-1 overflow-y-auto"
              aria-label={t('navigation:accessibility.mainNavigation', 'Main navigation')}
            >
              {filteredNavItems.map(item => {
                if (item.isGroup && item.children) {
                  // Render collapsible admin section (mobile)
                  const Icon = item.icon;
                  const isAnyAdminPageActive = item.children.some(
                    child => child.path && location.pathname === child.path
                  );

                  return (
                    <div key={item.label}>
                      <button
                        onClick={() => setAdminSectionExpanded(!adminSectionExpanded)}
                        className={clsx(
                          'flex items-center space-x-3 p-3 rounded-lg transition-colors w-full',
                          isAnyAdminPageActive
                            ? 'bg-accent-50 text-accent-600 dark:bg-accent-900/20 dark:text-accent-400'
                            : 'text-light-muted hover:bg-light-hover dark:text-dark-muted dark:hover:bg-dark-hover'
                        )}
                        aria-expanded={adminSectionExpanded}
                      >
                        <Icon size={20} aria-hidden="true" />
                        <span className="font-medium flex-1 text-left">{item.label}</span>
                        {adminSectionExpanded ? (
                          <ChevronDown size={16} aria-hidden="true" />
                        ) : (
                          <ChevronRight size={16} aria-hidden="true" />
                        )}
                      </button>

                      {/* Collapsible children */}
                      {adminSectionExpanded && (
                        <div className="ml-4 mt-1 space-y-1">
                          {item.children.map(child => {
                            const ChildIcon = child.icon;
                            const isActive = location.pathname === child.path;
                            const navId = child.path === '/' ? 'dashboard' : child.path?.slice(1);
                            return (
                              <Link
                                key={child.path}
                                to={child.path!}
                                data-testid={`nav-mobile-${navId}`}
                                onClick={() => setIsMobileMenuOpen(false)}
                                aria-current={isActive ? 'page' : undefined}
                                className={clsx(
                                  'flex items-center space-x-3 p-3 rounded-lg transition-colors',
                                  isActive
                                    ? 'bg-accent-50 text-accent-600 dark:bg-accent-900/20 dark:text-accent-400'
                                    : 'text-light-muted hover:bg-light-hover dark:text-dark-muted dark:hover:bg-dark-hover'
                                )}
                              >
                                <ChildIcon size={20} aria-hidden="true" />
                                <span className="font-medium">{child.label}</span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                } else {
                  // Render regular menu item (mobile)
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  const navId = item.path === '/' ? 'dashboard' : item.path?.slice(1);
                  return (
                    <Link
                      key={item.path}
                      to={item.path!}
                      data-testid={`nav-mobile-${navId}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                      aria-current={isActive ? 'page' : undefined}
                      className={clsx(
                        'flex items-center space-x-3 p-3 rounded-lg transition-colors',
                        isActive
                          ? 'bg-accent-50 text-accent-600 dark:bg-accent-900/20 dark:text-accent-400'
                          : 'text-light-muted hover:bg-light-hover dark:text-dark-muted dark:hover:bg-dark-hover'
                      )}
                    >
                      <Icon size={20} aria-hidden="true" />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  );
                }
              })}
            </nav>
            <div className="p-4 border-t border-light-border dark:border-dark-border">
              <button
                onClick={() => {
                  logout();
                  setIsMobileMenuOpen(false);
                }}
                className="flex items-center space-x-3 p-3 w-full text-error hover:bg-error-light dark:hover:bg-error/20 rounded-lg transition-colors"
              >
                <LogOut size={20} aria-hidden="true" />
                <span className="font-medium">{t('navigation:header.logout')}</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main
        id="main-content"
        className="flex-1 overflow-auto flex flex-col"
        role="main"
        aria-label={t('navigation:accessibility.mainContent', 'Main content')}
      >
        <header className="bg-white dark:bg-dark-card shadow-subtle dark:shadow-dark-subtle p-4 flex justify-between items-center sticky top-0 z-10 border-b border-light-border dark:border-dark-border">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsMobileMenuOpen(prev => !prev)}
              className="md:hidden text-light-muted hover:text-light-text dark:text-dark-muted dark:hover:text-dark-text"
              aria-label={t('navigation:accessibility.openMenu', 'Open navigation menu')}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              <Menu size={24} aria-hidden="true" />
            </button>
            <h2 className="text-lg sm:text-xl font-semibold text-light-text dark:text-dark-text">
              {filteredNavItems
                .flatMap(item => item.isGroup && item.children ? item.children : [item])
                .find(item => item.path === location.pathname)?.label || 'TechPulse'}
            </h2>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4">
            <StoreSelector />
            <LanguageToggle />
            <ThemeToggle />
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-accent-100 dark:bg-accent-900/30 rounded-full flex items-center justify-center text-accent-600 dark:text-accent-400 font-bold text-sm">
                {userName?.charAt(0).toUpperCase() || 'U'}
              </div>
              <span className="hidden sm:inline text-sm font-medium text-light-text dark:text-dark-text">
                {userName || t('navigation:user')}
              </span>
            </div>
          </div>
        </header>
        <div className="flex-1 p-4 sm:p-8 bg-light-card dark:bg-dark-bg">
          <Outlet />
        </div>
        {/* Bottom padding for mobile nav */}
        <div className="h-16 sm:hidden" />
      </main>

      {/* Mobile Bottom Navigation */}
      <BottomNav onMenuClick={() => setIsMobileMenuOpen(prev => !prev)} />

      {/* Floating Action Button */}
      <FloatingActionButton />
    </div>
  );
}
